import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Checkbox,
  Spinner,
  VStack,
  HStack,
  Image,
  Text,
  Flex,
  IconButton,
  useColorModeValue,
  Heading,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { useDropzone } from 'react-dropzone';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import api from '../api';
import { Select as ChakraReactSelect } from "chakra-react-select";


const CreateProduct = ({ isOpen, onClose, onProductCreated, initialData }) => {
  // Color mode values for modern styling
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const initialFormState = {
    name: '',
    description: '',
    price: '',
    purchase_price: '',
    stock_quantity: '',
    catalog_number: '',
    unit_measurement: 'шт',
    markup_percentage: 0,
    is_vat: false,
    categories: [],
    country: '',
    images: [],
  };

  const [formData, setFormData] = useState(initialFormState);
  const [categories, setCategories] = useState([[]]); // Top-level categories
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [removedImages, setRemovedImages] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [images, setImages] = useState([]);

  // Fetch categories hierarchy
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await api.get('/categories/hierarchy/');
      setCategories([response.data]); // Initialize with top-level categories
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (initialData) {
      // Populate form data with initialData
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        price: initialData.price || '',
        purchase_price: initialData.purchase_price || '',
        stock_quantity: initialData.stock_quantity || '',
        catalog_number: initialData.catalog_number || '',
        unit_measurement: initialData.unit_measurement || 'шт',
        markup_percentage: initialData.markup_percentage || 0,
        is_vat: initialData.is_vat || false,
        categories: initialData.categories || [],
        country: initialData.country || '',
        images: initialData.images || [],
      });
  
      // Populate images from initialData
      const processedImages =
        initialData.images?.map((image, index) => ({
          file: null,
          preview: image.preview,
          id: image.id,
          priority: image.priority || index + 1,
        })) || [];
      setImages(processedImages);
  
      // Initialize categories
      const fetchInitialCategories = async () => {
        try {
          const topLevelCategoriesResponse = await api.get('/categories/hierarchy/');
          setCategories([topLevelCategoriesResponse.data]);
  
          if (initialData.categories.length) {
            const resolvedCategories = [];
            for (const categoryId of initialData.categories) {
              const categoryResponse = await api.get(`/categories/${categoryId}/`);
              const category = categoryResponse.data;
  
              resolvedCategories.push(category);
              if (category.children && category.children.length > 0) {
                setCategories((prev) => [...prev, category.children]);
              }
            }
            setSelectedCategories(initialData.categories);
          }
        } catch (error) {
          console.error('Failed to fetch initial categories:', error);
        }
      };
  
      fetchInitialCategories();
    } else {
      // Reset state for creation mode
      setFormData(initialFormState);
      setSelectedCategories([]);
      setImages([]);
      setCategories([[]]);
    }
  }, [initialData]);
  
  

  // Fetch countries
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await api.get('/countries/');
        setCountries(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Failed to fetch countries:', error);
        setCountries([]); // Fallback to an empty array
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    if (isOpen) {
      setErrorMessage('');
    }
  }, [isOpen]);

  const handleClose = () => {
    // Reset all form fields, categories, and images
    setFormData(initialFormState);
    setSelectedCategories([]);
    setImages([]);
    fetchCategories(); // Reload categories for top-level options
    setErrorMessage('');
    onClose();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleCategoryChange = async (categoryId, level) => {
    const selected = [...selectedCategories];
    selected[level] = categoryId;
    setSelectedCategories(selected.slice(0, level + 1));
    setFormData({ ...formData, categories: selected });
  
    // Fetch children for the newly selected category
    try {
      const response = await api.get(`/categories/${categoryId}/`);
      const parent = response.data;
  
      if (parent.children && parent.children.length > 0) {
        const updatedCategories = [...categories];
        updatedCategories[level + 1] = parent.children; // Set children as the next level
        setCategories(updatedCategories.slice(0, level + 2));
      } else {
        setCategories(categories.slice(0, level + 1)); // Clear further levels if no children
      }
    } catch (error) {
      console.error('Failed to fetch child categories:', error);
    }
  };

  const onDrop = (acceptedFiles) => {
    if (images.length + acceptedFiles.length > 6) {
      alert('You can only upload up to 6 images.');
      return;
    }
    const newImages = acceptedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setImages((prev) => [...prev, ...newImages]);
  };

  const reorderImages = (result) => {
    if (!result.destination) return;
  
    const reorderedImages = Array.from(images);
    const [movedImage] = reorderedImages.splice(result.source.index, 1);
    reorderedImages.splice(result.destination.index, 0, movedImage);
  
    // Update priority based on the new order
    const updatedImages = reorderedImages.map((image, index) => ({
      ...image,
      priority: index + 1,
    }));
  
    setImages(updatedImages);
  };
  

  const removeImage = (index) => {
    setImages((prev) => {
      const removed = [...prev];
      const [removedImage] = removed.splice(index, 1);
  
      if (removedImage.id) {
        // If the image exists on the backend, add it to removedImages
        setRemovedImages((prevRemoved) => [...prevRemoved, removedImage.id]);
      }
  
      return removed;
    });
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
      };
  
      const response = initialData
        ? await api.put(`/products/${initialData.id}/`, payload)
        : await api.post('/products/', payload);
  
      const productId = response.data.id;
  
      const formDataToSend = new FormData();
  
      // Handle adding new images
      images.forEach((image, index) => {
        if (image.file) {
          formDataToSend.append(`image_${index + 1}`, image.file);
          formDataToSend.append(`priority_image_${index + 1}`, image.priority || index + 1);
        }
      });
  
      // Handle removed images
      if (removedImages.length > 0) {
        formDataToSend.append('remove_images', removedImages.join(','));
      }
  
      // Send formData to manage-images API
      if (images.length > 0 || removedImages.length > 0) {
        formDataToSend.append('product', productId);
  
        await api.post('/product-images/manage-images/', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
  
      // Handle reordering priorities
      const reorderedImages = images.filter((image) => !image.file); // Exclude newly added images
      for (const image of reorderedImages) {
        await api.patch(`/product-images/${image.id}/set-priority/`, {
          priority: image.priority,
        });
      }
  
      onProductCreated();
      handleClose(); // Clear fields and close popup
    } catch (error) {
      console.error('Failed to create/update product:', error);
      if (error.response?.data) {
        const errData = error.response.data;

        const translateError = (field, message) => {
          const translations = {
            name: 'Назва',
            purchase_price: 'Закупочна ціна',
            stock_quantity: 'Кількість на складі',
          };

          const messageTranslations = {
            'This field may not be blank.': 'Це поле не може бути порожнім.',
            'A valid number is required.': 'Потрібно ввести коректне число.',
            'A valid integer is required.': 'Потрібно ввести ціле число.',
          };

          const fieldUk = translations[field] || field;
          const msgUk = Array.isArray(message)
            ? message.map(m => messageTranslations[m] || m).join(', ')
            : messageTranslations[message] || message;

          return `${fieldUk}: ${msgUk}`;
        };

        if (typeof errData === 'object') {
          const messages = Object.entries(errData)
            .map(([field, message]) => translateError(field, message))
            .join('\n');
          setErrorMessage(messages);
        } else {
          setErrorMessage('Помилка при збереженні товару.');
        }
      } else {
        setErrorMessage('Сталася помилка. Спробуйте пізніше.');
      }
    }
  };
  
  const handlePurchasePriceChange = (e) => {
    const purchase_price = parseFloat(e.target.value) || 0;
    const markup_percentage = parseFloat(formData.markup_percentage) || 0;
    const price = purchase_price + (purchase_price * (markup_percentage / 100));
    setFormData((prev) => ({
      ...prev,
      purchase_price: e.target.value,
      price: price.toFixed(2),
    }));
  };

  const handleMarkupChange = (e) => {
    const inputValue = e.target.value;
    const markup_percentage = inputValue === '' ? 0 : parseFloat(inputValue);
    const purchase_price = parseFloat(formData.purchase_price) || 0;
    const price = purchase_price + (purchase_price * (markup_percentage / 100));

    setFormData((prev) => ({
      ...prev,
      markup_percentage: inputValue, // keep what user types
      price: price.toFixed(2),
    }));
  };

  const handlePriceChange = (e) => {
    const price = parseFloat(e.target.value) || 0;
    const purchase_price = parseFloat(formData.purchase_price) || 0;

    const markup_percentage =
      purchase_price > 0 ? ((price - purchase_price) / purchase_price) * 100 : 0;

    setFormData((prev) => ({
      ...prev,
      price: e.target.value,
      markup_percentage: markup_percentage.toFixed(2),
    }));
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    onDrop,
  });

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={{ base: 'full', md: '6xl' }} isCentered>
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent 
        maxH={{ base: '100vh', md: '90vh' }} 
        h={{ base: '100vh', md: '90vh' }} 
        bg={cardBg}
        borderRadius={{ base: 0, md: '2xl' }}
        border="1px solid"
        borderColor={borderColor}
        boxShadow={`0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}`}
        position="relative"
        overflow="hidden"
      >
        {/* Gradient accent */}
        <Box position="absolute" top={0} left={0} right={0} height="6px" bg="linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c)" />
        <ModalHeader pb={4} pt={{ base: 4, md: 8 }} bg={cardBg} borderTopRadius={{ base: 0, md: '2xl' }}>
          <Heading size={{ base: 'md', md: 'lg' }} bgGradient="linear(to-r, #667eea, #764ba2)" bgClip="text" fontWeight="bold">
            {initialData ? '✏️ Редагувати товар' : '➕ Створити новий товар'}
          </Heading>
        </ModalHeader>
        <ModalCloseButton top={{ base: 3, md: 6 }} right={{ base: 3, md: 6 }} size={{ base: 'md', md: 'lg' }} borderRadius="full" _hover={{ bg: hoverBg, color: textColor }} />
        <ModalBody overflowY="auto" bg={cardBg} p={{ base: 3, md: 6 }}>
          {errorMessage && (
            <Box mb="4" bg="linear-gradient(135deg, #FEE2E2, #FECACA)" p={{ base: 3, md: 4 }} borderRadius="xl" border="1px solid" borderColor="red.200" boxShadow="0 4px 6px rgba(239, 68, 68, 0.1)">
              <Text fontWeight="bold" color="red.700" mb={2}>⚠️ Помилка:</Text>
              <Text whiteSpace="pre-line" color="red.600">{errorMessage}</Text>
            </Box>
          )}
          <Flex gap={{ base: 4, md: 8 }} direction={{ base: 'column', md: 'row' }}>
            {/* Left Side - Modern Card */}
            <Box flex={{ base: '1 1 auto', md: '0 0 380px' }} bg={cardBg} borderRadius="xl" border="1px solid" borderColor={borderColor} p={{ base: 4, md: 6 }} boxShadow={`0 10px 15px -3px ${shadowColor}`} position="relative" overflow="hidden">
              <Box position="absolute" top={0} left={0} right={0} height="4px" bg="linear-gradient(90deg, #4facfe, #00f2fe)" opacity={0.8} />
              <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                <FormControl>
                  <FormLabel 
                    color={textColor} 
                    fontWeight="semibold"
                    mb={3}
                  >
                    📝 Назва
                  </FormLabel>
                  <Input 
                    name="name" 
                    value={formData.name} 
                    onChange={handleChange}
                    bg={cardBg}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="lg"
                    _focus={{
                      borderColor: "#4facfe",
                      boxShadow: "0 0 0 1px rgba(79, 172, 254, 0.3)",
                    }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel 
                    color={textColor} 
                    fontWeight="semibold"
                    mb={3}
                  >
                    🗂️ Категорії
                  </FormLabel>
                  <VStack spacing={3} align="stretch">
                    {categories.map((categoryList, level) => (
                      <Box key={level}>
                        <Select
                          placeholder="Оберіть категорію"
                          onChange={(e) => handleCategoryChange(parseInt(e.target.value), level)}
                          value={selectedCategories[level] || ''}
                          bg={cardBg}
                          border="1px solid"
                          borderColor={borderColor}
                          borderRadius="lg"
                          _focus={{
                            borderColor: "#4facfe",
                            boxShadow: "0 0 0 1px rgba(79, 172, 254, 0.3)",
                          }}
                        >
                          {Array.isArray(categoryList) &&
                            categoryList.map((category) => (
                              <option key={category.id} value={category.id}>
                                {category.name}
                              </option>
                            ))}
                        </Select>
                      </Box>
                    ))}
                  </VStack>
                </FormControl>
              </VStack>
            </Box>
            {/* Right Side - Modern Card */}
            <Box flex="1" bg={cardBg} borderRadius="xl" border="1px solid" borderColor={borderColor} p={{ base: 4, md: 6 }} boxShadow={`0 10px 15px -3px ${shadowColor}`} position="relative" overflow="hidden">
              <Box position="absolute" top={0} left={0} right={0} height="4px" bg="linear-gradient(90deg, #f093fb, #f5576c)" opacity={0.8} />
              <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                <FormControl>
                  <FormLabel 
                    color={textColor} 
                    fontWeight="semibold"
                    mb={3}
                  >
                    📄 Опис
                  </FormLabel>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    bg={cardBg}
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="lg"
                    resize="vertical"
                    minH="80px"
                    maxH="120px"
                    _focus={{
                      borderColor: "#f093fb",
                      boxShadow: "0 0 0 1px rgba(240, 147, 251, 0.3)",
                    }}
                  />
                </FormControl>

                {/* Row 1: Каталожний номер, Кількість на складі, Одиниця виміру */}
                <HStack spacing={3} align={{ base: 'center', md: 'flex-start' }}>
                  <FormControl flex="1">
                    <FormLabel color={textColor} fontWeight="semibold" mb={{ base: 1, md: 3 }} fontSize={{ base: 'xs', md: 'sm' }} whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                      <Text display={{ base: 'inline', md: 'none' }}>🏷️ Кат. номер</Text>
                      <Text display={{ base: 'none', md: 'inline' }}>🏷️ Каталожний номер</Text>
                    </FormLabel>
                    <Input name="catalog_number" value={formData.catalog_number} onChange={handleChange} bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="lg" size="sm" _focus={{ borderColor: '#f093fb', boxShadow: '0 0 0 1px rgba(240, 147, 251, 0.3)' }} />
                  </FormControl>
                  <FormControl flex="1">
                    <FormLabel color={textColor} fontWeight="semibold" mb={{ base: 1, md: 3 }} fontSize={{ base: 'xs', md: 'sm' }} whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                      <Text display={{ base: 'inline', md: 'none' }}>📦 Кількість</Text>
                      <Text display={{ base: 'none', md: 'inline' }}>📦 Кількість на складі</Text>
                    </FormLabel>
                    <Input type="number" name="stock_quantity" value={formData.stock_quantity} onChange={handleChange} bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="lg" size="sm" _focus={{ borderColor: '#f093fb', boxShadow: '0 0 0 1px rgba(240, 147, 251, 0.3)' }} />
                  </FormControl>
                  <FormControl flex="1">
                    <FormLabel color={textColor} fontWeight="semibold" mb={{ base: 1, md: 3 }} fontSize={{ base: 'xs', md: 'sm' }} whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                      <Text display={{ base: 'inline', md: 'none' }}>📏 Одиниця</Text>
                      <Text display={{ base: 'none', md: 'inline' }}>📏 Одиниця виміру</Text>
                    </FormLabel>
                    <Select name="unit_measurement" value={formData.unit_measurement} onChange={handleChange} bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="lg" size="sm" _focus={{ borderColor: '#f093fb', boxShadow: '0 0 0 1px rgba(240, 147, 251, 0.3)' }}>
                      <option value="шт">шт</option>
                      <option value="л">л</option>
                      <option value="кг">кг</option>
                      <option value="г">г</option>
                      <option value="млл">млл</option>
                      <option value="м">м</option>
                      <option value="мм">мм</option>
                    </Select>
                  </FormControl>
                </HStack>

                {/* Row 2: Закупочна ціна, Відсоток націнки, Ціна */}
                <HStack spacing={3} align={{ base: 'center', md: 'flex-start' }}>
                  <FormControl flex="1">
                    <FormLabel color={textColor} fontWeight="semibold" mb={{ base: 1, md: 3 }} fontSize={{ base: 'xs', md: 'sm' }} whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                      <Text display={{ base: 'inline', md: 'none' }}>💰 Закуп. ціна</Text>
                      <Text display={{ base: 'none', md: 'inline' }}>💰 Закупочна ціна</Text>
                    </FormLabel>
                    <Input type="number" name="purchase_price" value={formData.purchase_price} onChange={handlePurchasePriceChange} bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="lg" size="sm" _focus={{ borderColor: '#f093fb', boxShadow: '0 0 0 1px rgba(240, 147, 251, 0.3)' }} />
                  </FormControl>
                  <FormControl flex="1">
                    <FormLabel color={textColor} fontWeight="semibold" mb={{ base: 1, md: 3 }} fontSize={{ base: 'xs', md: 'sm' }} whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                      <Text display={{ base: 'inline', md: 'none' }}>📈 Націнка %</Text>
                      <Text display={{ base: 'none', md: 'inline' }}>📈 Відсоток націнки</Text>
                    </FormLabel>
                    <Input type="number" name="markup_percentage" value={formData.markup_percentage} onFocus={(e) => { if (e.target.value === '0') e.target.select(); }} onBlur={(e) => { if (e.target.value === '') { setFormData((prev) => ({ ...prev, markup_percentage: 0 })); } }} onChange={handleMarkupChange} bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="lg" size="sm" _focus={{ borderColor: '#f093fb', boxShadow: '0 0 0 1px rgba(240, 147, 251, 0.3)' }} />
                  </FormControl>
                  <FormControl flex="1">
                    <FormLabel color={textColor} fontWeight="semibold" mb={{ base: 1, md: 3 }} fontSize={{ base: 'xs', md: 'sm' }} whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                      <Text display={{ base: 'inline', md: 'none' }}>💲 Ціна</Text>
                      <Text display={{ base: 'none', md: 'inline' }}>💲 Ціна</Text>
                    </FormLabel>
                    <Input type="number" name="price" value={formData.price} onChange={handlePriceChange} bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="lg" size="sm" _focus={{ borderColor: '#f093fb', boxShadow: '0 0 0 1px rgba(240, 147, 251, 0.3)' }} />
                  </FormControl>
                </HStack>

                {/* Row 3: Країна виробник, Податок */}
                <HStack spacing={3} align="flex-start">
                  <FormControl maxW="250px">
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                      fontSize="sm"
                    >
                      🌍 Країна виробник
                    </FormLabel>
                    {loadingCountries ? (
                      <Spinner color="#f093fb" size="sm" />
                    ) : (
                      <ChakraReactSelect
                        name="country"
                        value={countries.find(c => c.id === formData.country) || null}
                        onChange={(selectedOption) =>
                          setFormData({ ...formData, country: selectedOption ? selectedOption.id : '' })
                        }
                        options={countries}
                        getOptionLabel={(e) => e.name}
                        getOptionValue={(e) => e.id}
                        placeholder="Оберіть країну"
                        isClearable
                        chakraStyles={{
                          control: (provided) => ({
                            ...provided,
                            bg: cardBg,
                            border: `1px solid ${borderColor}`,
                            borderRadius: "lg",
                            minHeight: "32px",
                            _focus: {
                              borderColor: "#f093fb",
                              boxShadow: "0 0 0 1px rgba(240, 147, 251, 0.3)",
                            }
                          }),
                          menu: (provided) => ({
                            ...provided,
                            zIndex: 1500,
                          })
                        }}
                      />
                    )}
                  </FormControl>
                  <FormControl flex="1">
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                      fontSize="sm"
                    >
                      🧾 Податок
                    </FormLabel>
                    <Box pt={1}>
                      <Checkbox
                        name="is_vat"
                        isChecked={formData.is_vat}
                        onChange={handleChange}
                        colorScheme="purple"
                        size="lg"
                      >
                        <Text color={textColor} fontSize="sm">З ПДВ</Text>
                      </Checkbox>
                    </Box>
                  </FormControl>
                </HStack>
              </VStack>

              {/* Images Section - Modern Card Style */}
              <Box
                mt={{ base: 4, md: 6 }}
                bg={cardBg}
                borderRadius="xl"
                border="1px solid"
                borderColor={borderColor}
                p={{ base: 4, md: 6 }}
                boxShadow={`0 8px 10px -3px ${shadowColor}`}
                position="relative"
                overflow="hidden"
              >
                  {/* Gradient accent */}
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    height="3px"
                    bg="linear-gradient(90deg, #23a6d5, #23d5ab)"
                  />
                  
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={4}
                    >
                      🖼️ Зображення товару
                    </FormLabel>
                <DragDropContext onDragEnd={reorderImages}>
                  <Droppable droppableId="images" direction="horizontal">
                    {(provided) => (
                      <HStack
                        mt={4}
                        spacing={4}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        overflowX="auto"
                      >
                        {images.map((image, index) => (
                          <Draggable
                            key={image.file ? image.file.name : image.id || index}
                            draggableId={image.file ? image.file.name : String(image.id || index)}
                            index={index}
                          >
                            {(provided) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                position="relative"
                                borderRadius="md"
                                border="1px solid #e2e8f0"
                                overflow="hidden"
                                boxSize="100px"
                              >
                                <Image
                                  src={image.preview}
                                  alt={`Preview ${index}`}
                                  boxSize="100px"
                                  objectFit="cover"
                                />
                                <IconButton
                                  icon={<CloseIcon />}
                                  size="sm"
                                  position="absolute"
                                  top="2px"
                                  right="2px"
                                  onClick={() => removeImage(index)}
                                />
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </HStack>
                    )}
                    </Droppable>
                  </DragDropContext>
                  <Box
                    {...getRootProps()}
                    border="2px dashed"
                    borderColor="blue.400"
                    p="6"
                    textAlign="center"
                    cursor="pointer"
                    borderRadius="xl"
                    bg="linear-gradient(135deg, #EBF4FF, #E6FFFA)"
                    mt="4"
                    transition="all 0.2s ease"
                    _hover={{
                      borderColor: "blue.500",
                      bg: "linear-gradient(135deg, #DBEAFE, #D1FAE5)",
                      transform: "translateY(-1px)",
                    }}
                  >
                    <input {...getInputProps()} />
                    <Text 
                      color="blue.600" 
                      fontWeight="medium"
                      fontSize="md"
                    >
                      📁 Перетягніть або натисніть для вибору зображень
                    </Text>
                  </Box>
                </FormControl>
              </Box>
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter bg={cardBg} borderBottomRadius={{ base: 0, md: '2xl' }} p={{ base: 3, md: 6 }}>
          <Flex w="full" justify="flex-end" gap={{ base: 2, md: 4 }} wrap="wrap">
            <Button bg="linear-gradient(135deg, #667eea, #764ba2)" color="white" size={{ base: 'md', md: 'lg' }} mr={0} onClick={handleSubmit} borderRadius="xl" px={{ base: 6, md: 8 }} boxShadow="0 4px 15px rgba(102, 126, 234, 0.4)" _hover={{ bg: 'linear-gradient(135deg, #5a6fd8, #6a4190)' }}>
              {initialData ? '✏️ Редагувати' : '➕ Створити'}
            </Button>
            <Button variant="ghost" onClick={handleClose} size={{ base: 'md', md: 'lg' }} borderRadius="xl" px={{ base: 6, md: 8 }} color={textColor} _hover={{ bg: hoverBg }}>
              ❌ Скасувати
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateProduct;
